/**
 * Bar Chart Widget (Story 5.5 → Epic 37 redesign on shadcn/ui charts)
 *
 * Categorical comparison via shadcn ChartContainer (Recharts v3, height-enforced
 * so it renders). Size-aware via the shared chart shell: narrow OR short drops
 * grid/legend. Navy series, clean axes (no spines, faint horizontal grid). Anim
 * off for PDF parity. Grouped current/previous bars preserved.
 */

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { ChartContainer } from "../../ui/chart";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { CategoricalData, WidgetDataEnvelope } from "../../../lib/widgetData";
import { isCategoricalData } from "../../../lib/widgetData";
import { metricKeysFromConfig } from "../../../lib/widgetCompatibility";
import { useChartWidgetShell, ChartMessage, NAVY_SERIES, seriesChannelSuffix, compactNumber } from "./chartWidgetShell";

interface BarChartWidgetProps {
  widget: WidgetConfig;
  data?: WidgetDataEnvelope<CategoricalData>;
}

/**
 * Auto-calculate Y-axis gutter width from the longest tick label. Measures the
 * SAME compact label the YAxis tickFormatter renders ("21.5K"), and — crucially —
 * scales px-per-char by the actual axis `fontSize` so a larger font reserves a
 * proportionally wider gutter instead of overflowing the card (the bug: width was
 * hardcoded for ~11px text). ~0.62×fontSize is a safe per-char estimate for the
 * tabular digits used here.
 */
export function getYAxisWidth(categories: Record<string, unknown>[], metrics: string[], fontSize: number): number {
  let maxLen = 0;
  for (const cat of categories) {
    for (const metric of metrics) {
      const val = cat[metric];
      if (typeof val === "number") {
        maxLen = Math.max(maxLen, compactNumber(val).length);
      }
    }
  }
  const perChar = fontSize * 0.62;
  return Math.max(30, Math.ceil(maxLen * perChar) + 10);
}

/** At/above this axis font size, X labels always tilt (a large horizontal label
 *  reads as "big" and is the case the user wants angled regardless of fit). */
export const X_TILT_FONT_THRESHOLD = 12;

/**
 * Decide whether X-axis category labels should tilt (-35°). Tilts when EITHER:
 *  - the axis font is large (≥ X_TILT_FONT_THRESHOLD) — the user wants big labels
 *    angled even when they technically fit; OR
 *  - the longest label is wider than ~85% of its category band (geometric overlap).
 * Band width comes from the REAL measured widget width (shell ResizeObserver) minus
 * the Y gutter + right margin; before first layout / in jsdom (widgetWidth = 0) it
 * falls back to a colSpan proxy.
 */
export function shouldTiltXLabels(args: {
  widgetWidth: number;
  colSpan: number;
  yAxisWidth: number;
  categoryCount: number;
  labelPx: number;
  fontSize: number;
}): boolean {
  const { widgetWidth, colSpan, yAxisWidth, categoryCount, labelPx, fontSize } = args;
  if (fontSize >= X_TILT_FONT_THRESHOLD) return true;
  const outerPx = widgetWidth > 0 ? widgetWidth - 32 /* card padding */ : colSpan * 96;
  const plotWidthPx = outerPx - yAxisWidth - 20;
  const bandPx = Math.max(plotWidthPx, 0) / Math.max(categoryCount, 1);
  return labelPx > bandPx * 0.85;
}

/**
 * Build the X-axis tick render function. recharts ignores `angle`/`textAnchor` on a
 * plain styles-object `tick`, so we render the <text> ourselves and apply the
 * rotation via `transform` — this is what makes the labels actually tilt. Exported
 * for tests (jsdom can't lay out recharts, so we verify the emitted <text> props).
 */
export function makeXTickRenderer(opts: {
  tilt: boolean;
  angle: number;
  fontSize: number;
  color: string;
  fontFamily?: string;
}) {
  const { tilt, angle, fontSize, color, fontFamily } = opts;
  return function XTick(props: { x?: string | number; y?: string | number; payload?: { value?: unknown } }) {
    const x = Number(props.x ?? 0);
    const y = Number(props.y ?? 0);
    const text = String(props.payload?.value ?? "");
    return (
      <text
        x={x}
        y={y}
        dy={tilt ? 4 : 12}
        textAnchor={tilt ? "end" : "middle"}
        transform={tilt ? `rotate(${angle}, ${x}, ${y})` : undefined}
        fontSize={fontSize}
        fill={color}
        {...(fontFamily ? { fontFamily } : {})}
      >
        {text}
      </text>
    );
  };
}

const COMPACT_MARGINS = { top: 5, right: 5, bottom: 5, left: 5 };

export function BarChartWidget({ widget, data }: BarChartWidgetProps) {
  const config = widget.config as { metrics?: string[]; groupBy?: string };
  // Normalize the metric binding (string[] / object[] / metricKey) to plain keys.
  const boundMetrics = metricKeysFromConfig(widget.config as Record<string, unknown>);
  const groupBy = config.groupBy ?? "";
  const colSpan = widget.position.colSpan;
  const { compact, width: widgetWidth, height: widgetHeight, shell } = useChartWidgetShell(widget.config);

  if (data?.loading) {
    return shell("bar-chart", widget.title, <div className="h-full min-h-[60px] w-full animate-pulse rounded bg-muted" />);
  }
  if (data?.error) {
    return shell("bar-chart", widget.title, <ChartMessage role="alert" testId="bar-chart-error">{data.error}</ChartMessage>, { centered: true });
  }

  const chartData = data?.data;
  if (!chartData || !isCategoricalData(chartData) || chartData.categories.length === 0) {
    return shell("bar-chart", widget.title, <ChartMessage testId="bar-chart-no-data">No data available</ChartMessage>, { centered: true });
  }
  // An UNBOUND chart has no configured metrics — derive the series from the data
  // itself (every numeric key of the first category) so sample data still draws bars.
  const firstCat = (chartData.categories[0] ?? {}) as Record<string, unknown>;
  const metrics =
    boundMetrics.length > 0
      ? boundMetrics
      : Object.keys(firstCat).filter((k) => typeof firstCat[k] === "number");
  if (colSpan <= 2 && !compact) {
    return shell("bar-chart", widget.title, <ChartMessage testId="chart-placeholder">Widen widget to view chart</ChartMessage>, { centered: true });
  }

  const isCompact = colSpan <= 4 || compact;
  const hasComparison = Boolean(chartData.comparisonCategories && chartData.comparisonCategories.length > 0);

  // Appearance > "Axis labels" — color / font / size on the X & Y tick labels.
  const axisStyle = (widget.config?.style ?? {}) as { textColor?: string; fontFamily?: string; fontSize?: number; seriesColors?: Record<string, string>; seriesLabels?: Record<string, string>; legendFontSize?: number; legendMarker?: "line" | "dot" | "none"; legendMarkerSize?: number };
  const axisTick = (defaultSize: number) => ({
    fontSize: typeof axisStyle.fontSize === "number" ? axisStyle.fontSize : defaultSize,
    fill: axisStyle.textColor ?? "hsl(var(--muted-foreground))",
    ...(axisStyle.fontFamily ? { fontFamily: axisStyle.fontFamily } : {}),
  });
  // Y-axis gutter scales with the RESOLVED tick font size (default 9 compact / 11
  // full, or the user's Appearance > Axis text size) so big labels never overflow.
  const yTickFontSize = typeof axisStyle.fontSize === "number" ? axisStyle.fontSize : (isCompact ? 9 : 11);
  const yAxisWidth = getYAxisWidth(chartData.categories, metrics, yTickFontSize);

  // ── Axis-clipping fixes (font/size aware) ──────────────────────────────────
  // The default fixed 5px margins don't account for the tick font, so the top Y
  // label and the X labels clip when the font grows or the X labels are long/many.
  // X tick font honours the user's Appearance > Axis text size whenever set — the
  // SAME resolution as the Y axis — falling back to the compact/full default only
  // when there's no override. (Bug fix: X used to be forced to 9px on compact cards
  // so it never grew with the setting while Y did.)
  const hasCustomAxisFont = typeof axisStyle.fontSize === "number";
  const xTickFontSize = hasCustomAxisFont ? (axisStyle.fontSize as number) : (isCompact ? 9 : 11);
  // Longest X label, in chars — drives whether we tilt and how much bottom gutter.
  const longestXLabel = chartData.categories.reduce(
    (max, cat) => Math.max(max, String(cat[groupBy] ?? "").length),
    0,
  );
  // Tilt X labels when a category band is too narrow for its horizontal label, OR
  // the user picked a large axis font (≥ threshold). The auto/geometric tilt is
  // suppressed on tiny compact cards, but an EXPLICIT font override still tilts even
  // there (otherwise setting a big size on a compact card silently does nothing).
  // `interval={0}` (below) keeps EVERY label so recharts can't drop overlapping ones.
  const approxLabelPx = longestXLabel * xTickFontSize * 0.62;
  const tiltX =
    (!isCompact || hasCustomAxisFont) &&
    shouldTiltXLabels({
      widgetWidth: widgetWidth,
      colSpan,
      yAxisWidth,
      categoryCount: chartData.categories.length,
      labelPx: approxLabelPx,
      fontSize: xTickFontSize,
    });
  const xAngle = tiltX ? -35 : 0;
  // X-axis band height — the vertical room the labels need below the plot.
  //  • tilted: text projects down by ~sin(35°)·labelWidth (+ tickMargin + pad).
  //  • horizontal: a full glyph line is ~font×1.35 (caps + descenders) + pad.
  // CAPPED at ~40% of the measured widget height so a huge font can never push the
  // plot off-screen (the bug: an uncapped tilted gutter ate the whole chart). The
  // cap only applies once we have a real measurement (widgetHeight > 0).
  const rawXBand = tiltX
    ? Math.ceil(approxLabelPx * 0.57) + 6 + 8
    : Math.ceil(xTickFontSize * 1.35) + 6 + 6;
  const xBandHeight = widgetHeight > 0 ? Math.min(rawXBand, Math.floor(widgetHeight * 0.4)) : rawXBand;
  // Top gutter: the top Y tick is centered on y=0, so half the glyph sits above the
  // plot — reserve half the font (+pad) so it isn't clipped by the card edge.
  const topMargin = Math.ceil(yTickFontSize / 2) + 4;
  // The X band is reserved via the XAxis `height` prop below — NOT also via
  // margin.bottom (that would double-count and shrink the plot twice). Keep
  // margin.bottom minimal; only the top gutter needs an explicit margin.
  const chartMargin =
    isCompact && !hasCustomAxisFont
      ? COMPACT_MARGINS
      : { top: topMargin, right: 20, bottom: 5, left: 0 };

  // Custom X-axis tick (see makeXTickRenderer): recharts does NOT apply
  // `angle`/`textAnchor` to a plain styles-object `tick`, so we render the rotated
  // <text> ourselves — this is what actually tilts the labels.
  const renderXTick = makeXTickRenderer({
    tilt: tiltX,
    angle: xAngle,
    fontSize: xTickFontSize,
    color: axisStyle.textColor ?? "hsl(var(--muted-foreground))",
    fontFamily: axisStyle.fontFamily,
  });
  // Per-series colour (Appearance > Bar colors): user pick per metric, else default.
  const seriesColors = axisStyle.seriesColors ?? {};
  const seriesColor = (metric: string, i: number) => seriesColors[metric] ?? NAVY_SERIES[i % NAVY_SERIES.length];
  // Per-series label (Appearance > Bar labels): friendly legend name, else raw key.
  // " · <Channel>" suffix appended when the chart mixes integrations.
  const seriesLabels = axisStyle.seriesLabels ?? {};
  const channelSuffix = seriesChannelSuffix(widget.config as Record<string, unknown>, metrics);
  const seriesLabel = (metric: string) => (seriesLabels[metric]?.trim() || metric) + channelSuffix(metric);
  // Legend text size (Appearance > Bars > Legend size); default 11.
  const legendFontSize = typeof axisStyle.legendFontSize === "number" ? axisStyle.legendFontSize : 11;
  // Legend marker: default swatch / dot / none (none colours the label text).
  // DOT and NONE render their own inline-centered marker (the built-in icon doesn't
  // vertically center a large circle against the text); the default swatch keeps
  // the built-in rect.
  const legendMarker = axisStyle.legendMarker ?? "line";
  const markerSize = typeof axisStyle.legendMarkerSize === "number" ? axisStyle.legendMarkerSize : undefined;
  const legendIconType = "rect" as const;
  const legendIconSize = legendMarker === "line" ? markerSize : 0;
  const dotPx = markerSize ?? 8;
  const legendFormatter =
    legendMarker === "dot"
      ? (value: string, entry: { color?: string }) => (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, verticalAlign: "middle" }}>
            <span style={{ width: dotPx, height: dotPx, borderRadius: "9999px", background: entry?.color ?? "currentColor", display: "inline-block" }} />
            <span>{value}</span>
          </span>
        )
      : legendMarker === "none"
        ? (value: string, entry: { color?: string }) => (
            <span style={{ color: entry?.color ?? "inherit" }}>{value}</span>
          )
        : undefined;

  const mergedData = hasComparison
    ? chartData.categories.map((cat, i) => {
        const compCat = chartData.comparisonCategories![i];
        const merged: Record<string, string | number> = { ...cat };
        if (compCat) {
          for (const metric of metrics) {
            merged[`prev_${metric}`] = compCat[metric] as number;
          }
        }
        return merged;
      })
    : chartData.categories;

  return shell(
    "bar-chart",
    widget.title,
    <div className="min-h-0 w-full flex-1" role="img" aria-label={`Bar chart showing ${metrics.join(", ")} by ${groupBy}`}>
      <ChartContainer config={{}} className="h-full w-full">
        <BarChart data={mergedData} margin={chartMargin}>
          {!isCompact && <CartesianGrid stroke="hsl(214 25% 70% / 0.18)" vertical={false} />}
          <XAxis
            dataKey={groupBy}
            tick={renderXTick}
            tickLine={false}
            axisLine={false}
            tickMargin={6}
            // interval=0 forces EVERY category label to render (full cards, or any
            // card with a custom axis font) so recharts never silently drops
            // overlapping ones — the custom tick above applies the rotation.
            // `height` reserves the (capped) X-label band so the plot keeps room.
            {...(!isCompact || hasCustomAxisFont ? { interval: 0 as const, height: xBandHeight } : {})}
          />
          <YAxis
            tick={axisTick(isCompact ? 9 : 11)}
            width={yAxisWidth}
            tickLine={false}
            axisLine={false}
            tickMargin={4}
            // Compact labels ("21.5K") keep the gutter narrow; width above is sized
            // to this same formatting so the labels always fit inside the card.
            tickFormatter={(v) => (typeof v === "number" ? compactNumber(v) : String(v))}
          />
          {!isCompact && <Tooltip cursor={{ fill: "hsl(214 25% 70% / 0.10)" }} contentStyle={{ borderRadius: 10, border: "0.5px solid var(--hairline)", fontSize: 12 }} />}
          {!isCompact && (hasComparison || metrics.length > 1) && <Legend wrapperStyle={{ fontSize: legendFontSize, paddingTop: 24 }} iconType={legendIconType} iconSize={legendIconSize} formatter={legendFormatter} verticalAlign="bottom" />}
          {metrics.map((metric, i) => (
            <Bar
              key={metric}
              dataKey={metric}
              name={hasComparison ? "Current Period" : seriesLabel(metric)}
              fill={seriesColor(metric, i)}
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
            />
          ))}
          {hasComparison && metrics.map((metric, i) => (
            <Bar
              key={`prev_${metric}`}
              dataKey={`prev_${metric}`}
              name="Previous Period"
              // Same hue as its current-period bar, lighter via opacity.
              fill={seriesColor(metric, i)}
              radius={[4, 4, 0, 0]}
              opacity={0.55}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ChartContainer>
    </div>,
  );
}
